/**
 * The Days section of a well job (api#1062): one row per day from the
 * mobilization day to demobilization (or today), each with its kind, the
 * depths the office recorded, any state override, and the day's ticket.
 *
 * EVERY FIGURE IS THE API'S. Quantities, rates, amounts, the day's footage, the
 * subtotal and the ticket total are printed as returned; the browser adds,
 * multiplies and rounds nothing. An absent figure prints "—", never $0, and an
 * unpriced day says why in the API's own words (`problems[]`). Line quantities
 * are printed in the unit the API priced them in (a footage line is in feet for
 * every tenant): converting the quantity alone would make quantity × rate stop
 * reading as the amount beside it.
 *
 * WRITES:
 *   depths and the state override: DOWNHOLE_JOB_WRITE_ROLES (PUT …/days/:date);
 *     an override only on an on-location day, and it needs a reason
 *   the day's ticket: DOWNHOLE_TICKET_WRITE_ROLES, admin and office_staff
 *     (POST …/days/:date/ticket); it then goes through the existing ticket
 *     screen, approve, send, sign
 * A save re-prices the day and can send its ticket back to draft; the API names
 * the tickets it reverted, and the dialog says so. A customer-signed day
 * refuses the change, naming the ticket (DAY_TICKET_CUSTOMER_APPROVED).
 *
 * Depths are typed in the tenant's units and stored in feet, as on the run
 * dialogs. The rows hold a Price button, so they are not row targets.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useJobDay, useJobDays, useOpenDayTicket, useUpdateJobDay } from '../../hooks/useDownholeJobs';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency, fmtQty } from '../../lib/display-labels';
import { isJobClosed } from '../../lib/downhole-jobs';
import { DH_DAY_KIND_LABEL, DH_DAY_STATE_LABEL, DH_DAY_UNIT_LABEL, dhLabel } from '../../lib/downhole-labels';
import { onlineOrSay } from '../../lib/downhole-offline';
import { dhUnitLabel, formatDh, toInputValue, toStoredImperial } from '../../lib/downhole-units';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_DAY_STATES, type DhDayPrice, type DhDayWrite, type DhJobDay, type DhJobDetail, type DhUnitSystem } from '../../types/downhole';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhEmpty, DhField, DhPanel } from './DownholeBits';
import { RefusalNote, TicketStatusBadge, apiErrorProblems } from './ticketBits';

const PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';
const DAY_FORMAT: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };


export function DaysPanel({
  job,
  units,
  canWriteDays,
  canTicket,
}: {
  job: DhJobDetail;
  units: DhUnitSystem | null;
  canWriteDays: boolean;
  canTicket: boolean;
}) {
  const query = useJobDays(job.id, true);
  const failure = queryLoadFailure(query);
  const days = query.data?.data ?? [];
  const currency = query.data?.meta?.currency ?? null;
  const [open, setOpen] = useState<string | null>(null);

  return (
    <DhPanel eyebrow="Billing" title="Days" testId="well-days">
      {query.isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading days" /></div>
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this job's days" />
      ) : days.length === 0 ? (
        <DhEmpty>No days yet. Days start when the job is mobilized.</DhEmpty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left">
            <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-4 py-3">Day</th>
                <th scope="col" className="px-4 py-3">Kind</th>
                <th scope="col" className="px-4 py-3 text-right">Depth start</th>
                <th scope="col" className="px-4 py-3 text-right">Depth end</th>
                <th scope="col" className="px-4 py-3">State set by office</th>
                <th scope="col" className="px-4 py-3">Ticket</th>
                <th scope="col" className="px-4 py-3 text-right">Ticket total</th>
                <th scope="col" className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <DayRow key={d.work_date} day={d} units={units} currency={currency} onOpen={() => setOpen(d.work_date)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && (
        <DayDialog
          job={job}
          date={open}
          units={units}
          canWriteDays={canWriteDays}
          canTicket={canTicket}
          onClose={() => setOpen(null)}
        />
      )}
    </DhPanel>
  );
}

function DayRow({ day, units, currency, onOpen }: { day: DhJobDay; units: DhUnitSystem | null; currency: string | null; onOpen: () => void }) {
  return (
    <tr className="border-t border-fieldiq-border" data-testid={`day-${day.work_date}`}>
      <td className="whitespace-nowrap px-4 py-3 text-sm font-bold text-white">{formatCalendarDate(day.work_date, DAY_FORMAT) ?? '—'}</td>
      <td className="px-4 py-3 text-sm text-fieldiq-text">{dhLabel(DH_DAY_KIND_LABEL, day.day_kind)}</td>
      <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(day.depth_start_ft, 'length', units, { digits: 0 })}</td>
      <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{formatDh(day.depth_end_ft, 'length', units, { digits: 0 })}</td>
      <td className="px-4 py-3 text-sm text-fieldiq-text">
        {day.state_override ? (
          <>
            <span className="font-bold">{dhLabel(DH_DAY_STATE_LABEL, day.state_override)}</span>
            {day.override_reason && <span className="block text-xs text-fieldiq-muted">{day.override_reason}</span>}
          </>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-3 text-sm">
        {day.ticket_id ? (
          <span className="inline-flex items-center gap-2">
            <Link to={`/downhole/tickets/${day.ticket_id}`} className="font-mono font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
              {day.ticket_number ?? '—'}
            </Link>
            <TicketStatusBadge status={day.ticket_status} />
            {/* api#1090: the live invoice this day is billed on. */}
            {day.invoice_id && (
              <Link to={`/downhole/invoices/${day.invoice_id}`} className="font-mono text-xs font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
                on {day.invoice_number ?? 'an invoice'}
              </Link>
            )}
          </span>
        ) : (
          <span className="text-fieldiq-muted">No ticket</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm text-fieldiq-text">{day.ticket_id ? fmtCurrency(day.ticket_total, currency) : '—'}</td>
      <td className="px-4 py-3 text-right">
        <button type="button" className={DH_SECONDARY} onClick={onOpen} aria-label={`Price ${day.work_date}`}>
          Price
        </button>
      </td>
    </tr>
  );
}

type Values = { depth_start: string; depth_end: string; state_override: string; override_reason: string };

const valuesOf = (p: DhDayPrice, units: DhUnitSystem | null): Values => ({
  depth_start: toInputValue(p.depth_start_ft, 'length', units),
  depth_end: toInputValue(p.depth_end_ft, 'length', units),
  state_override: p.state_override ?? '',
  override_reason: p.override_reason ?? '',
});

/**
 * What a day save sends: only the inputs that changed. A depth opened and saved
 * untouched is not sent (a metric depth is not a round trip). Clearing the
 * override sends null and no reason; the API clears the reason with it.
 */
export function dayWriteBody(v: Values, before: Values, units: DhUnitSystem | null): DhDayWrite {
  const body: DhDayWrite = {};
  if (v.depth_start.trim() !== before.depth_start.trim()) body.depth_start_ft = toStoredImperial(v.depth_start, 'length', units, 1);
  if (v.depth_end.trim() !== before.depth_end.trim()) body.depth_end_ft = toStoredImperial(v.depth_end, 'length', units, 1);
  if (v.state_override !== before.state_override) {
    body.state_override = (v.state_override || null) as DhDayWrite['state_override'];
    if (v.state_override) body.override_reason = v.override_reason.trim() || null;
  } else if (v.state_override && v.override_reason.trim() !== before.override_reason.trim()) {
    body.override_reason = v.override_reason.trim() || null;
  }
  return body;
}

function DayDialog({
  job,
  date,
  units,
  canWriteDays,
  canTicket,
  onClose,
}: {
  job: DhJobDetail;
  date: string;
  units: DhUnitSystem | null;
  canWriteDays: boolean;
  canTicket: boolean;
  onClose: () => void;
}) {
  const query = useJobDay(job.id, date);
  const failure = queryLoadFailure(query);
  const price = query.data?.data ?? null;
  const tenantUnits = query.data?.meta?.unit_system ?? units;

  return (
    <DhDialog open wide title={`${formatCalendarDate(date, DAY_FORMAT) ?? date}: ${job.job_number}`} onClose={onClose}>
      {query.isLoading ? (
        <div className="flex justify-center py-10"><Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading the day" /></div>
      ) : failure || !price ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this day's price" />
      ) : (
        <DayBody job={job} date={date} price={price} units={tenantUnits} canWriteDays={canWriteDays} canTicket={canTicket} onClose={onClose} />
      )}
    </DhDialog>
  );
}

function DayBody({
  job,
  date,
  price,
  units,
  canWriteDays,
  canTicket,
  onClose,
}: {
  job: DhJobDetail;
  date: string;
  price: DhDayPrice;
  units: DhUnitSystem | null;
  canWriteDays: boolean;
  canTicket: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const update = useUpdateJobDay();
  const openTicket = useOpenDayTicket();
  const before = valuesOf(price, units);
  const [v, setV] = useState<Values>(before);
  const [refusal, setRefusal] = useState<{ message: string; problems: string[] } | null>(null);
  const [reverted, setReverted] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const closed = isJobClosed(job.status);
  const editable = canWriteDays && !closed;
  const onLocation = price.day_kind === 'on_location';
  const set = (k: keyof Values) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setV((s) => ({ ...s, [k]: e.target.value }));

  const save = () => {
    setRefusal(null);
    const body = dayWriteBody(v, before, units);
    if (Object.keys(body).length === 0) return;
    if (body.state_override && !v.override_reason.trim()) return setRefusal({ message: 'Say why this day is marked that way.', problems: [] });
    if (!onlineOrSay((m) => setRefusal({ message: m, problems: [] }))) return;
    update.mutate(
      { jobId: job.id, date, body },
      {
        onSuccess: (r) => {
          setReverted(r.revertedTickets);
          toast.success('Day saved');
        },
        onError: (err) => setRefusal({ message: getApiErrorMessage(err, 'The day could not be saved.'), problems: apiErrorProblems(err) }),
      },
    );
  };

  const createTicket = () => {
    setRefusal(null);
    if (!onlineOrSay((m) => setRefusal({ message: m, problems: [] }))) return;
    openTicket.mutate(
      { jobId: job.id, date, body: notes.trim() ? { notes: notes.trim() } : {} },
      {
        onSuccess: (r) => {
          toast.success(`Ticket ${r.data?.ticket_number ?? ''} opened`.trim());
          onClose();
          if (r.data?.id) navigate(`/downhole/tickets/${r.data.id}`);
        },
        onError: (err) => setRefusal({ message: getApiErrorMessage(err, 'The ticket could not be opened.'), problems: apiErrorProblems(err) }),
      },
    );
  };

  return (
    <div className="space-y-4" data-testid="day-price">
      <div className="grid gap-3 sm:grid-cols-4">
        <Fact label="Kind" value={dhLabel(DH_DAY_KIND_LABEL, price.day_kind)} />
        <Fact label="State" value={price.state ? `${dhLabel(DH_DAY_STATE_LABEL, price.state)}${price.state_overridden ? ' (set by office)' : ''}` : '—'} />
        <Fact label="Footage" value={price.footage === null || price.footage === undefined ? '—' : `${fmtQty(price.footage)} ft`} />
        <Fact label="Subtotal" value={price.complete ? fmtCurrency(price.subtotal, price.currency) : '—'} />
      </div>

      {!price.complete && (
        <div role="status" data-testid="day-problems" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
          <p className="font-bold">This day cannot be priced yet.</p>
          {price.problems.length > 0 && (
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {price.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-fi-control border border-fieldiq-border">
        <table className="w-full min-w-[640px] text-left" aria-label="Day price lines">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr>
              <th scope="col" className="px-3 py-2">Line</th>
              <th scope="col" className="px-3 py-2 text-right">Quantity</th>
              <th scope="col" className="px-3 py-2 text-right">Rate</th>
              <th scope="col" className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {price.lines.length === 0 ? (
              <tr><td colSpan={4} className="px-3 py-4 text-center text-sm text-fieldiq-text">No lines for this day.</td></tr>
            ) : (
              price.lines.map((l, i) => (
                <tr key={`${l.line_type}-${l.dh_job_crew_id ?? l.component_id ?? i}`} className="border-t border-fieldiq-border">
                  <td className="px-3 py-2 text-sm text-fieldiq-text">
                    {l.description}
                    {l.amount === null && l.unpriced_reason && <span className="block text-xs text-fieldiq-muted">{l.unpriced_reason}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-sm text-fieldiq-text">
                    {fmtQty(l.quantity)} {l.quantity === null ? '' : dhLabel(DH_DAY_UNIT_LABEL, l.unit)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(l.rate, l.currency ?? price.currency)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-sm text-fieldiq-text">{fmtCurrency(l.amount, l.currency ?? price.currency)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editable && (
        <div className="space-y-3 rounded-fi-control border border-fieldiq-border p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <DhField label={`Depth at start (${dhUnitLabel('length', units)})`}>
              <input className={DH_INPUT} inputMode="decimal" value={v.depth_start} onChange={set('depth_start')} aria-label="Depth at start" />
            </DhField>
            <DhField label={`Depth at end (${dhUnitLabel('length', units)})`}>
              <input className={DH_INPUT} inputMode="decimal" value={v.depth_end} onChange={set('depth_end')} aria-label="Depth at end" />
            </DhField>
            {onLocation && (
              <>
                <DhField label="Day state" hint="Leave on Automatic to let the runs decide.">
                  <select className={DH_INPUT} value={v.state_override} onChange={set('state_override')} aria-label="Day state">
                    <option value="">Automatic</option>
                    {DH_DAY_STATES.map((s) => <option key={s} value={s}>{DH_DAY_STATE_LABEL[s]}</option>)}
                  </select>
                </DhField>
                {v.state_override && (
                  <DhField label="Reason *">
                    <input className={DH_INPUT} value={v.override_reason} maxLength={2000} onChange={set('override_reason')} aria-label="Reason" />
                  </DhField>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end">
            <button type="button" className={PRIMARY} onClick={save} disabled={update.isPending}>
              {update.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Save day
            </button>
          </div>
        </div>
      )}

      {reverted.length > 0 && (
        <p role="status" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text">
          {reverted.length === 1 ? 'Ticket' : 'Tickets'} {reverted.join(', ')} went back to draft to be re-priced before anyone signs {reverted.length === 1 ? 'it' : 'them'}.
        </p>
      )}

      <RefusalNote refusal={refusal} />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-fieldiq-border pt-3">
        {price.ticket ? (
          <span className="inline-flex items-center gap-2 text-sm text-fieldiq-text">
            Ticket
            <Link to={`/downhole/tickets/${price.ticket.id}`} className="font-mono font-bold text-fieldiq-accent-signal underline-offset-2 hover:underline">
              {price.ticket.ticket_number}
            </Link>
            <TicketStatusBadge status={price.ticket.status} />
          </span>
        ) : canTicket && job.status !== 'cancelled' ? (
          <div className="flex flex-1 flex-wrap items-end gap-2">
            <div className="min-w-[14rem] flex-1">
              <DhField label="Ticket notes (office only)">
                <input className={DH_INPUT} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} aria-label="Ticket notes" />
              </DhField>
            </div>
            <button type="button" className={PRIMARY} onClick={createTicket} disabled={openTicket.isPending}>
              {openTicket.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Open day ticket
            </button>
          </div>
        ) : (
          <span className="text-sm text-fieldiq-muted">No ticket for this day.</span>
        )}
        <button type="button" className={DH_SECONDARY} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">{label}</p>
      <p className="mt-0.5 text-sm font-bold text-white">{displayValue(value as any)}</p>
    </div>
  );
}
